import type { ApiResponse, ContactSummaryDto } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

/**
 * Key prefix shared by every identity-scoped summary entry. Exported so
 * mutations that change what the summary aggregates (e.g. collab actions
 * feeding the rail's "{n} en cours" count) can invalidate all summaries by
 * prefix without knowing the identity.
 */
export const CONTACT_SUMMARY_QUERY_PREFIX = ['contact-summary'] as const;

/** Shared key so any consumer targets the same cache entry. */
export const contactSummaryQueryKey = (
  brandId: string | null | undefined,
  creatorIdentityId: string | null
) => [...CONTACT_SUMMARY_QUERY_PREFIX, brandId ?? '', creatorIdentityId] as const;

export const contactSummaryQueryPrefix = (brandId: string | null | undefined) =>
  [...CONTACT_SUMMARY_QUERY_PREFIX, brandId ?? ''] as const;

const EMPTY_SUMMARY: ContactSummaryDto = {
  socialProfiles: [],
  conversation: null,
  activeIgConversationId: null,
  hasEmail: false,
  emailOrigin: null,
  affiliation: null,
  ads: { activeCount: 0 },
  collabs: null,
  addedOn: null,
};

export const fetchContactSummary = async (
  creatorIdentityId: string
): Promise<ContactSummaryDto> => {
  const { data } = await api.get<ApiResponse<ContactSummaryDto>>(
    `/dashboard/contacts/${creatorIdentityId}/summary`
  );

  return data.data ?? EMPTY_SUMMARY;
};

/**
 * Aggregate header/rail slice for the unified contact card, keyed by
 * `creatorIdentityId`. Only fires once a contact is resolved. Returns which
 * social profiles exist, the id of the active Instagram DM thread (if any) and
 * whether the contact is emailable, the affiliation standing and the live
 * partnership-ad count (all empty until the request settles).
 */
export const useContactSummaryQuery = (
  brandId: string | null | undefined,
  creatorIdentityId: string | null
) => {
  return useQuery({
    queryKey: contactSummaryQueryKey(brandId, creatorIdentityId),
    queryFn: () => fetchContactSummary(creatorIdentityId!),
    enabled: !!brandId && !!creatorIdentityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
